import { ContentChild, Directive, OnDestroy, OnInit, Self } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReportOutputBase } from '@ndt/report-output/shared/models/report-output-base';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { closeFullscreenTile, openFullscreenTile } from 'src/app/pages/dashboard/state/dashboard.actions';
import { getTileViewMode } from '../../../models/tile-view-mode.model';
import { deleteDashboardTile, tileLoad } from '../../../state/tile.actions';
import { VerboseTileIds } from '../../../state/tile.reducer';
import { TileViewComponent } from '../tile-view/tile-view.component';
import { TileParametersDefDirective } from './tile-parameters-def.directive';

// eslint-disable-next-line @typescript-eslint/naming-convention
export type ExtractData<R> = R extends ReportOutputBase<infer D, any> ? D : never;
// eslint-disable-next-line @typescript-eslint/naming-convention
export type ExtractParameters<R> = R extends ReportOutputBase<any, infer P> ? P : never;

@Directive({
    selector: '[ndtTileContainer]',
    standalone: false
})
// eslint-disable-next-line @typescript-eslint/naming-convention
export class TileContainerDirective<R extends ReportOutputBase<any, any>, P = ExtractParameters<R>>
   implements OnDestroy, OnInit
{
   @ContentChild(TileParametersDefDirective, { static: true })
   tileParametersDirective: TileParametersDefDirective;

   private destroyed$ = new Subject<void>();

   private dialogId;

   constructor(
      @Self() private tileComponent: TileViewComponent,
      private confirmationDialogService: ConfirmationDialogService,
      private dialog: MatDialog,
      private store: Store,
      protected readonly actions$: Actions
   ) {}

   ngOnInit(): void {
      this.tileComponent.configureParameters.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         this.openParametersDialog();
      });
      this.tileComponent.openFullscreen.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         this.openFullscreen();
      });
      this.tileComponent.closeFullscreen.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         this.closeFullscreen();
      });
      this.tileComponent.deleteTile.pipe(takeUntil(this.destroyed$)).subscribe((dashboardTileId) => {
         this.deleteTile(dashboardTileId);
      });
      this.tileComponent.refreshTile.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         if (!this.tileComponent.overrideRefreshTile) {
            this.refreshTile();
         }
      });
   }

   ngOnDestroy(): void {
      this.closeDialogs();
      this.destroyed$.next();
   }

   private openFullscreen(): void {
      const tileConfig = {
         ...this.tileComponent.tileConfig,
         dashboardTileLayoutConfiguration: {
            ...this.tileComponent.tileConfig.dashboardTileLayoutConfiguration,
            height: Infinity,
            width: Infinity,
         },
      };

      this.store.dispatch(
         openFullscreenTile({
            config: {
               ...tileConfig,
               viewMode: getTileViewMode(tileConfig, true, false),
               dashboardTileId: VerboseTileIds.fullscreenTile,
            },
         })
      );
   }

   private closeFullscreen(): void {
      this.store.dispatch(closeFullscreenTile());
   }

   private refreshTile(): void {
      this.store.dispatch(
         tileLoad({
            dashboardTileId: this.tileComponent.tileConfig.dashboardTileId,
            isFullscreen: false,
            forceViewModeRecalculation: false,
         })
      );
   }

   private openParametersDialog(): void {
      const ref = this.dialog.open(this.tileParametersDirective.templateRef, {
         disableClose: true,
         panelClass: [DialogSize.Medium, 'dialog-container'],
      });

      // matDialog generates a unique id if none is given to it
      this.dialogId = ref.id;
   }

   private closeDialogs(): void {
      if (this.dialogId) {
         this.dialog.getDialogById(this.dialogId)?.close();
      }
   }

   private deleteTile(dashboardTileId: number): void {
      const dialogRef = this.confirmationDialogService.confirm({
         data: {
            header: 'Delete Tile?',
            message: 'Are you sure you want to delete this tile?',
         },
         size: DialogSize.Medium,
      });
      dialogRef.afterClosed().subscribe((confirmed) => {
         if (confirmed) {
            this.store.dispatch(deleteDashboardTile({ dashboardTileId }));
         }
      });
   }
}
